import type { GeneratePromptShareTemplate, Prompt, PromptCreate, PromptInformation, PromptShareTemplate, PromptUpdate, PromptUseCreate } from '../types/prompt'
import type { ElementalQuery } from '../api/elementals'
import type { Elemental, OwnershipInformation } from '../types/elemental'
import { APIFetch, APINativeFetch, filterResponse, filterResponseBoolean } from '.'

export const getPrompts = async (queries?: ElementalQuery): Promise<null | Elemental[]> => {
  let url = '/prompts'

  if (queries)
    url = useParseQuery(url, queries)

  return filterResponse(await APIFetch(url, {
    method: 'GET',
  }))
}

export const createPrompt = async (body: PromptCreate): Promise<null | Prompt> => {
  return filterResponse(await APIFetch('/prompts', {
    method: 'POST',
    body,
  }))
}

export const getPrompt = async (promptId: string): Promise<null | Prompt> => {
  return filterResponse(await APIFetch(`/prompts/${promptId}`, {
    method: 'GET',
  }))
}

// Returns true if success
export const updatePrompt = async (promptId: string, body: PromptUpdate): Promise<null | Prompt> => {
  return filterResponse(await APIFetch(`/prompts/${promptId}`, {
    method: 'PATCH',
    body,
  }))
}

// Returns true if success
export const deletePrompt = async (promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/prompts/${promptId}`, {
    method: 'DELETE',
  }))
}

// Returns true if success
export const votePrompt = async (promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/prompts/${promptId}/votes`, {
    method: 'POST',
  }))
}

// Returns true if success
export const removePromptVote = async (promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/prompts/${promptId}/votes`, {
    method: 'DELETE',
  }))
}

export const savePrompt = async (promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/prompts/${promptId}/saves`, {
    method: 'POST',
  }))
}

export const removeSavedPrompt = async (promptId: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/prompts/${promptId}/saves`, {
    method: 'DELETE',
  }))
}

// Register prompt use
export const usePrompt = async (promptId: string, body: PromptUseCreate): Promise<boolean> => {
  return filterResponseBoolean(await APINativeFetch(`/prompts/${promptId}/uses`, {
    method: 'POST',
    body,
  }))
}

export const getPromptInformation = async (promptIds: string[]): Promise<null | PromptInformation[]> => {
  return filterResponse(await APIFetch<PromptInformation[]>(useParseQuery('/prompt-information', { promptIds: promptIds.join(',') }), {
    method: 'GET',
  }))
}

export const getDefaultPromptShareBackgrounds = async (): Promise<string[] | null> => {
  return filterResponse(await APIFetch('/backgrounds', { method: 'GET', baseUrl: useRuntimeConfig().public.spMediagenApiUrl }))
}

export const getDefaultPromptShareTemplates = async (): Promise<PromptShareTemplate[] | null> => {
  return filterResponse(await APIFetch('/templates', { method: 'GET', baseUrl: useRuntimeConfig().public.spMediagenApiUrl }))
}

export const generatePromptShareImage = async (payload: GeneratePromptShareTemplate): Promise<string | null> => {
  return filterResponse(await APIFetch('/', { method: 'POST', baseUrl: useRuntimeConfig().public.spMediagenApiUrl, body: payload }))
}

export const uploadPromptShareBackground = async (file: File): Promise<string | null> => {
  const multipart = new FormData()
  multipart.append('background', file)

  return filterResponse(await APIFetch('/backgrounds', {
    method: 'POST',
    baseUrl: useRuntimeConfig().public.spMediagenApiUrl,
    body: multipart,
    isFormData: true,
  }))
}

export const uploadPromptAvatar = async (promptId: string, avatar: File, path?: string, isAdmin?: boolean) => {
  const formData = new FormData()
  formData.append('file', avatar)

  return filterResponseBoolean(await APIFetch(`${isAdmin ? '/admin' : ''}/${path || 'prompts'}/${promptId}/avatar`, {
    method: 'PATCH',
    body: formData,
    isFormData: true,
  }))
}

export const uploadPromptImage = async (promptId: string, image: File, imageType: 'cover' | 'output', path?: string, isAdmin?: boolean) => {
  const formData = new FormData()
  formData.append('file', image)

  return filterResponse(await APIFetch(`${isAdmin ? '/admin' : ''}/${path || 'prompts'}/${promptId}/images?${imageType === 'output' ? 'is_output_image' : 'is_cover_image'}=true`, {
    method: 'POST',
    body: formData,
    isFormData: true,
  }))
}

export const deletePromptImage = async (promptId: string, imageId: number, isAdmin?: boolean) => {
  return filterResponseBoolean(await APIFetch(`${isAdmin ? '/admin' : ''}/prompts/${promptId}/images/${imageId}`, {
    method: 'DELETE',
  }))
}

export const getPromptOwnershipInformation = async (promptId: string | number, isAdmin?: boolean): Promise<null | OwnershipInformation> => {
  return filterResponse(await APIFetch<OwnershipInformation>(`${isAdmin ? '/admin' : ''}/prompts/${promptId}/information`, {
    method: 'GET',
    baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
  }))
}

export const buyPrompt = async (promptId: string, value?: number): Promise<null | string> => {
  return filterResponse(await APIFetch<string>(`/prompts/${promptId}/buy?value=${value}`, {
    method: 'POST',
    baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
  }))
}

export const viewPrompt = async (promptIdOrUrl: string): Promise<boolean> => {
  return filterResponseBoolean(await APIFetch(`/prompts/${promptIdOrUrl}/views`, {
    method: 'POST',
    baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
  }))
}

export const tipPrompt = async (id: string, value: number): Promise<string | null> => {
  let url = `/tips/${id}/prompt`
  url = useParseQuery(url, { value })

  return filterResponse(await APIFetch(url, {
    method: 'POST',
    body: { value },
    baseUrl: useRuntimeConfig().public.marketplaceApiUrl,
  }))
}
